/**
 * Checkpoints de replay por sessão (ACK + last replayed).
 */

import { openQueueDb, STORE_REPLAY } from '../storage/idbQueueDb';
import { getCurrentSessionSequence } from '../ordering/sessionSequence';
import type { QueuePartition } from '../queue/partitioning';

const LS_KEY = 'GOMARKE_REPLAY_CURSOR_V2';
const memory = new Map<string, ReplayCheckpoint>();

export interface ReplayCheckpoint {
  sessionId: string;
  lastAckedSequence: number;
  lastReplayedSequence: number;
  generation: number;
  updatedAt: number;
  lastReplayedEventId?: string;
  lastReplayedPartition?: QueuePartition;
  lastSuccessfulBatchId?: string;
  replayCursor?: { sessionId: string; sequence: number };
}

function normalize(cp: Partial<ReplayCheckpoint> & { sessionId: string }): ReplayCheckpoint {
  return {
    sessionId: cp.sessionId,
    lastAckedSequence: cp.lastAckedSequence ?? 0,
    lastReplayedSequence: cp.lastReplayedSequence ?? 0,
    generation: cp.generation ?? 0,
    updatedAt: cp.updatedAt ?? Date.now(),
    lastReplayedEventId: cp.lastReplayedEventId,
    lastReplayedPartition: cp.lastReplayedPartition,
    lastSuccessfulBatchId: cp.lastSuccessfulBatchId,
    replayCursor: cp.replayCursor,
  };
}

async function idbPutCheckpoint(cp: ReplayCheckpoint): Promise<void> {
  const db = await openQueueDb();
  if (!db) return;
  return new Promise((resolve, reject) => {
    try {
      const tx = db.transaction(STORE_REPLAY, 'readwrite');
      tx.objectStore(STORE_REPLAY).put(cp);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    } catch {
      resolve();
    }
  });
}

async function idbGetCheckpoint(sessionId: string): Promise<ReplayCheckpoint | null> {
  const db = await openQueueDb();
  if (!db) return null;
  return new Promise(resolve => {
    try {
      const tx = db.transaction(STORE_REPLAY, 'readonly');
      const req = tx.objectStore(STORE_REPLAY).get(sessionId);
      req.onsuccess = () => resolve((req.result as ReplayCheckpoint) ?? null);
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

function loadLs(): Record<string, ReplayCheckpoint> {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(LS_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as Record<string, ReplayCheckpoint>;
  } catch {
    return {};
  }
}

function persistLs(cursors: Record<string, ReplayCheckpoint>): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(LS_KEY, JSON.stringify(cursors));
  } catch {
    // quota
  }
}

async function getCheckpoint(sessionId: string): Promise<ReplayCheckpoint> {
  if (memory.has(sessionId)) return memory.get(sessionId)!;
  const idb = await idbGetCheckpoint(sessionId);
  if (idb) {
    const cp = normalize(idb);
    memory.set(sessionId, cp);
    return cp;
  }
  const ls = loadLs()[sessionId];
  if (ls) {
    const cp = normalize(ls);
    memory.set(sessionId, cp);
    return cp;
  }
  return normalize({ sessionId });
}

async function saveCheckpoint(cp: ReplayCheckpoint): Promise<void> {
  memory.set(cp.sessionId, cp);
  const merged = { ...loadLs(), ...Object.fromEntries(memory) };
  persistLs(merged);
  await idbPutCheckpoint(cp);
}

export function bumpCheckpointGeneration(sessionId: string): void {
  const cp = memory.get(sessionId) ?? normalize({ sessionId });
  cp.generation += 1;
  cp.updatedAt = Date.now();
  memory.set(sessionId, cp);
  const all = loadLs();
  all[sessionId] = cp;
  persistLs(all);
  void idbPutCheckpoint(cp);
}

export async function hydrateAllCheckpointsFromIdb(): Promise<void> {
  const db = await openQueueDb();
  if (!db) return;
  return new Promise(resolve => {
    try {
      const tx = db.transaction(STORE_REPLAY, 'readonly');
      const req = tx.objectStore(STORE_REPLAY).getAll();
      req.onsuccess = () => {
        const rows = (req.result as ReplayCheckpoint[]) ?? [];
        const ls = loadLs();
        for (const row of rows) {
          const cp = normalize(row);
          const existing = memory.get(cp.sessionId) ?? ls[cp.sessionId];
          if (
            !existing ||
            cp.lastAckedSequence >= (existing.lastAckedSequence ?? 0)
          ) {
            memory.set(cp.sessionId, cp);
            ls[cp.sessionId] = cp;
          }
        }
        persistLs(ls);
        resolve();
      };
      req.onerror = () => resolve();
    } catch {
      resolve();
    }
  });
}

export async function getLastAckedSequence(sessionId: string): Promise<number> {
  return (await getCheckpoint(sessionId)).lastAckedSequence;
}

export function getLastAckedSequenceSync(sessionId: string): number {
  return memory.get(sessionId)?.lastAckedSequence ?? loadLs()[sessionId]?.lastAckedSequence ?? 0;
}

export async function setLastAckedSequence(
  sessionId: string,
  sequence: number
): Promise<void> {
  const cp = await getCheckpoint(sessionId);
  cp.lastAckedSequence = Math.max(cp.lastAckedSequence, sequence);
  cp.updatedAt = Date.now();
  await saveCheckpoint(cp);
}

export function setLastAckedSequenceSync(sessionId: string, sequence: number): void {
  const cp = memory.get(sessionId) ?? normalize({ sessionId });
  cp.lastAckedSequence = Math.max(cp.lastAckedSequence, sequence);
  cp.updatedAt = Date.now();
  memory.set(sessionId, cp);
  const all = loadLs();
  all[sessionId] = cp;
  persistLs(all);
  void idbPutCheckpoint(cp);
}

export async function getLastReplayedSequence(sessionId: string): Promise<number> {
  return (await getCheckpoint(sessionId)).lastReplayedSequence;
}

export function getLastReplayedSequenceSync(sessionId: string): number {
  return (
    memory.get(sessionId)?.lastReplayedSequence ??
    loadLs()[sessionId]?.lastReplayedSequence ??
    0
  );
}

export async function setLastReplayedSequence(
  sessionId: string,
  sequence: number
): Promise<void> {
  const cp = await getCheckpoint(sessionId);
  cp.lastReplayedSequence = Math.max(cp.lastReplayedSequence, sequence);
  cp.updatedAt = Date.now();
  await saveCheckpoint(cp);
}

export function setLastReplayedSequenceSync(
  sessionId: string,
  sequence: number,
  meta?: {
    eventId?: string;
    partition?: QueuePartition;
    batchId?: string;
  }
): void {
  const cp = memory.get(sessionId) ?? normalize({ sessionId });
  cp.lastReplayedSequence = Math.max(cp.lastReplayedSequence, sequence);
  cp.replayCursor = { sessionId, sequence: cp.lastReplayedSequence };
  if (meta?.eventId) cp.lastReplayedEventId = meta.eventId;
  if (meta?.partition) cp.lastReplayedPartition = meta.partition;
  if (meta?.batchId) cp.lastSuccessfulBatchId = meta.batchId;
  cp.updatedAt = Date.now();
  memory.set(sessionId, cp);
  const all = loadLs();
  all[sessionId] = cp;
  persistLs(all);
  void idbPutCheckpoint(cp);
}

/** Elegível para envio se o backend ainda não confirmou (ACK) esta sequência. */
export function shouldReplayItem(
  sessionId: string,
  sessionSequence: number
): boolean {
  const cp = memory.get(sessionId) ?? loadLs()[sessionId];
  const lastAcked = cp?.lastAckedSequence ?? 0;
  if (sessionSequence > lastAcked) return true;
  const counter = getCurrentSessionSequence(sessionId);
  // Contador de sessão reiniciou com checkpoint antigo — liberar faixa atual.
  if (lastAcked > counter && sessionSequence <= counter) {
    bumpCheckpointGeneration(sessionId);
    return true;
  }
  return false;
}

export function getCheckpointGeneration(sessionId: string): number {
  return memory.get(sessionId)?.generation ?? loadLs()[sessionId]?.generation ?? 0;
}

const REPLAY_CURSOR_TTL_MS = 7 * 24 * 60 * 60 * 1000;

export async function pruneStaleReplayCheckpoints(): Promise<void> {
  const db = await openQueueDb();
  if (!db) return;
  const cutoff = Date.now() - REPLAY_CURSOR_TTL_MS;
  return new Promise(resolve => {
    try {
      const tx = db.transaction(STORE_REPLAY, 'readwrite');
      const store = tx.objectStore(STORE_REPLAY);
      const req = store.getAll();
      req.onsuccess = () => {
        const rows = (req.result as ReplayCheckpoint[]) ?? [];
        for (const row of rows) {
          if ((row.updatedAt ?? 0) < cutoff) {
            store.delete(row.sessionId);
            memory.delete(row.sessionId);
          }
        }
        resolve();
      };
      req.onerror = () => resolve();
    } catch {
      resolve();
    }
  });
}

/** Restaura checkpoint do snapshot sem regredir abaixo do ACK. */
export function restoreCheckpointSnapshot(
  sessionId: string,
  lastAcked: number,
  lastReplayed: number
): void {
  const cp = memory.get(sessionId) ?? normalize({ sessionId });
  const ackFloor = Math.max(cp.lastAckedSequence, lastAcked);
  cp.lastAckedSequence = ackFloor;
  cp.lastReplayedSequence = Math.max(ackFloor, Math.min(cp.lastReplayedSequence, lastReplayed));
  cp.replayCursor = { sessionId, sequence: cp.lastReplayedSequence };
  cp.updatedAt = Date.now();
  memory.set(sessionId, cp);
  const all = loadLs();
  all[sessionId] = cp;
  persistLs(all);
  void idbPutCheckpoint(cp);
}

export function _testResetReplayCursor(): void {
  memory.clear();
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.removeItem(LS_KEY);
      window.localStorage.removeItem('GOMARKE_REPLAY_CURSOR_V1');
    } catch {
      // ignore
    }
  }
}
